import React, { lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import App from "./App";
import LoginPage from "./pages/LoginPage";
import TenantSelectionPage from "./pages/TenantSelectionPage";
import PrintDashboardPage from "./pages/PrintDashboardPage";

import 'bootstrap/dist/css/bootstrap.min.css';
import "./index.css";

// ResizeObserver 에러 전역 처리
window.addEventListener('error', (event) => {
  // ResizeObserver loop completed with undelivered notifications 에러 무시
  if (event.message && event.message.includes('ResizeObserver loop completed')) {
    event.preventDefault();
    event.stopPropagation();
    console.debug('ResizeObserver 에러 무시됨:', event.message);
    return false;
  }
});

// 처리되지 않은 Promise rejection도 처리
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop completed')) {
    event.preventDefault();
    console.debug('ResizeObserver Promise rejection 무시됨:', event.reason.message);
    return false;
  }
});

// AdminPage를 동적 import로 로드
const AdminPage = React.lazy(() => import('./pages/AdminPage'));

// API 기본 URL 설정
const API_URL = process.env.REACT_APP_API_URL || '/api';

// 전역 axios 인터셉터 설정 - 모든 axios 인스턴스에 적용
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 에러 처리 (인증 만료/실패)
    if (error.response && error.response.status === 401) {
      // 현재 페이지가 로그인 페이지이거나 테넌트 선택 페이지인 경우 리다이렉트하지 않음
      const currentPath = window.location.pathname;
      if (currentPath === '/login' || currentPath === '/select-tenant') {
        console.log('[Global Interceptor] 로그인/테넌트 선택 페이지에서 401 에러 - 리다이렉트 생략');
        return Promise.reject(error);
      }
      
      console.warn('[Global Interceptor] JWT 인증 실패 감지. 로그인 페이지로 리다이렉트합니다.');
      console.error('인증 실패 URL:', error.config?.url);

      // 동시접속 제한으로 인한 세션 종료 메시지 저장
      const detail = error.response?.data?.detail || '';
      if (detail.includes('다른 곳에서 로그인')) {
        sessionStorage.setItem('session_expired_message', detail);
      }

      // 현재 페이지 URL을 저장하여 로그인 후 돌아올 수 있도록 함
      sessionStorage.setItem('redirect_after_login', window.location.pathname);
      
      // 쿠키 정리 (dashboard_id는 백엔드에서 관리)
      document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'current_tenant=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      // 로그인 페이지로 즉시 리다이렉트
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 인증 상태에 따라 라우팅을 제어하는 컴포넌트
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(null);
  const [hasTenant, setHasTenant] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  
  React.useEffect(() => {
    // 인증 상태 확인
    const checkAuth = async () => {
      try {
        // 사용자 정보 API 호출 (인증 확인)
        const response = await axios.get(`${API_URL}/users/me/`, {
          withCredentials: true // 쿠키를 포함하여 요청
        });
        
        setIsAuthenticated(true);
        
        // 테넌트 컨텍스트 확인
        try {
          const tenantResponse = await axios.get(`${API_URL}/debug/tenant/`, {
            withCredentials: true
          });
          
          // 테넌트가 설정되어 있는지 확인
          setHasTenant(!!tenantResponse.data.current_tenant);
        } catch (tenantError) {
          console.error('테넌트 확인 오류:', tenantError);
          setHasTenant(false);
        }
        
      } catch (error) {
        console.error('인증 확인 오류:', error);
        setIsAuthenticated(false);
        setHasTenant(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // 로딩 중일 때 표시
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  // 인증되지 않은 경우 로그인 페이지로 이동
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // 테넌트가 선택되지 않았고 테넌트 선택 페이지가 아니면 테넌트 선택 페이지로 이동
  if (!hasTenant && window.location.pathname !== '/select-tenant') {
    return <Navigate to="/select-tenant" replace />;
  }
  
  return children;
};

// 관리자 전용 라우트 컴포넌트
const AdminRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(null);
  const [isAdmin, setIsAdmin] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  
  React.useEffect(() => {
    // 인증 및 관리자 권한 확인
    const checkAuthAndAdmin = async () => {
      try {
        // 사용자 정보 API 호출 (인증 확인)
        const response = await axios.get(`${API_URL}/users/me/`, {
          withCredentials: true // 쿠키를 포함하여 요청
        });
        
        setIsAuthenticated(true);
        
        // 관리자 페이지 접근은 슈퍼관리자만 허용
        const hasAdminPermission = !!response.data.is_superuser;
        setIsAdmin(hasAdminPermission);
        
      } catch (error) {
        console.error('관리자 인증 확인 오류:', error);
        setIsAuthenticated(false);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthAndAdmin();
  }, []);
  
  // 로딩 중일 때 표시
  if (isLoading) {
    return <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontSize: '18px'
    }}>관리자 권한 확인 중...</div>;
  }
  
  // 인증되지 않은 경우 로그인 페이지로 이동
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // 슈퍼관리자 권한이 없는 경우 메인 페이지로 이동
  if (!isAdmin) {
    alert('슈퍼관리자 권한이 필요합니다.');
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route 
          path="/select-tenant" 
          element={
            <ProtectedRoute>
              <TenantSelectionPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <React.Suspense fallback={<div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                fontSize: '18px'
              }}>관리자 페이지를 로딩 중...</div>}>
                <AdminPage />
              </React.Suspense>
            </AdminRoute>
          } 
        />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <App />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/print" 
          element={
            <ProtectedRoute>
              <PrintDashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="*" 
          element={
            <ProtectedRoute>
              <App />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);
